import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaBars, FaBell, FaSignOutAlt, FaComments, FaFileInvoice, FaCalendarAlt, FaClipboardCheck, FaCreditCard, FaFolderOpen, FaCheck, FaExpand, FaCompress, FaUser, FaChevronDown, FaTrashAlt, FaChevronRight } from "react-icons/fa";
import { useTheme } from "../context/ThemeContext";
import { useToast } from "../context/ToastContext";
import Breadcrumbs from "./ui/Breadcrumbs";
import ThemeCustomizer from "./ui/ThemeCustomizer";
import io from "socket.io-client";

// Relative time helper
const timeAgo = (date) => {
  const now = new Date();
  const diff = Math.floor((now - new Date(date)) / 1000);
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(date).toLocaleDateString();
};

// Notification type icon + color mapping
const getNotifStyle = (type) => {
  switch (type) {
    case "chat":
      return { icon: FaComments, bg: "bg-blue-100 dark:bg-blue-900/40", color: "text-blue-600 dark:text-blue-400" };
    case "invoice":
      return { icon: FaFileInvoice, bg: "bg-emerald-100 dark:bg-emerald-900/40", color: "text-emerald-600 dark:text-emerald-400" };
    case "meeting":
      return { icon: FaCalendarAlt, bg: "bg-purple-100 dark:bg-purple-900/40", color: "text-purple-600 dark:text-purple-400" };
    case "checklist":
      return { icon: FaClipboardCheck, bg: "bg-amber-100 dark:bg-amber-900/40", color: "text-amber-600 dark:text-amber-400" };
    case "payment":
    case "service_purchase":
      return { icon: FaCreditCard, bg: "bg-pink-100 dark:bg-pink-900/40", color: "text-pink-600 dark:text-pink-400" };
    case "document":
      return { icon: FaFolderOpen, bg: "bg-cyan-100 dark:bg-cyan-900/40", color: "text-cyan-600 dark:text-cyan-400" };
    default:
      return { icon: FaBell, bg: "bg-gray-100 dark:bg-dark-700", color: "text-gray-600 dark:text-gray-400" };
  }
};

const AdminHeader = ({ setSidebarOpen }) => {
  const [notifications, setNotifications] = useState([]);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [adminProfile, setAdminProfile] = useState({
    name: '',
    email: '',
    profileImage: '',
  });
  const navigate = useNavigate();
  const notifDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const toast = useToast();
  const socketRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const displayName = adminProfile.name?.trim() || 'My Profile';
  const profileSubtitle = adminProfile.email?.trim() || '';
  const adminInitial = displayName[0]?.toUpperCase() || 'A';

  useEffect(() => {
    const loadAdminProfile = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setAdminProfile({
          name: localStorage.getItem('adminName') || '',
          email: '',
          profileImage: '',
        });
        return;
      }

      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/admin-profile/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load profile');
        const data = await res.json();
        setAdminProfile({
          name: data.profile?.name || localStorage.getItem('adminName') || '',
          email: data.profile?.email || '',
          profileImage: data.profile?.profileImage || '',
        });
        if (data.profile?.name) localStorage.setItem('adminName', data.profile.name);
      } catch {
        setAdminProfile({
          name: localStorage.getItem('adminName') || '',
          email: '',
          profileImage: '',
        });
      }
    };

    loadAdminProfile();
  }, []);

  // Fetch notifications from new API
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notifications/admin`);
      if (!response.ok) throw new Error("Failed to fetch notifications.");
      const data = await response.json();
      setNotifications(data);
    } catch (error) {
      console.error("Error fetching notifications:", error.message);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000); // poll every 15s as fallback
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const lastToastRef = useRef({ text: '', time: 0 });

  // Socket.io for real-time notification push and toast alerts
  useEffect(() => {
    const socket = io(process.env.REACT_APP_API_BASE_URL, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('joinRoom', 'admin');
    });

    const triggerToast = (text, type = 'info') => {
      const now = Date.now();
      // Prevent duplicate toasts within 2 seconds
      if (lastToastRef.current.text === text && now - lastToastRef.current.time < 2000) {
        return;
      }
      lastToastRef.current = { text, time: now };
      if (type === 'chat') {
        toast.info(`💬 ${text}`, 5000);
      } else if (type === 'document') {
        toast.info(`📎 ${text}`, 5000);
      } else {
        toast.info(`🔔 ${text}`, 5000);
      }
    };

    socket.on('newNotification', (notification) => {
      setNotifications(prev => [notification, ...prev]);
      if (notification) {
        // Skip chat type notifications in toast because newMessage listener handles real-time chat toasts cleanly
        if (notification.type === 'chat') return;

        const title = notification.title || 'New Notification';
        const msg = notification.message || '';
        const combined = msg ? `${title}: ${msg}` : title;
        const toastType = notification.type === 'document' ? 'document' : 'info';
        triggerToast(combined, toastType);
      }
    });

    socket.on('newMessage', (data) => {
      if (data && data.sender === 'user') {
        const userName = data.user || 'Client';
        const isFile = data.fileUrl || data.originalName || (data.message && data.message.startsWith('File uploaded:'));
        if (isFile) {
          const fileName = data.originalName || (data.message ? data.message.replace(/^File uploaded:\s*/, '') : 'Document');
          triggerToast(`${userName} uploaded document: ${fileName}`, 'document');
        } else if (data.message) {
          const preview = data.message.length > 60 ? `${data.message.substring(0, 60)}...` : data.message;
          triggerToast(`${userName}: "${preview}"`, 'chat');
        }
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [toast]);
  
  // Fullscreen logic
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`);
      });
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Handle logout
  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      if (token) {
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/admin-logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (error) {
      console.error("Error recording admin logout:", error);
    } finally {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminName");
      localStorage.removeItem("adminID");
      setShowProfileDropdown(false);
      toast.success("Logged out successfully");
      navigate("/login");
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notifDropdownRef.current
        && !notifDropdownRef.current.contains(event.target)
        && !event.target.closest('.notification-btn')
      ) {
        setShowNotifDropdown(false);
      }

      if (
        profileDropdownRef.current
        && !profileDropdownRef.current.contains(event.target)
        && !event.target.closest('.profile-menu-btn')
      ) {
        setShowProfileDropdown(false);
      }
    };

    if (showNotifDropdown || showProfileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifDropdown, showProfileDropdown]);

  const handleProfileNavigate = () => {
    setShowProfileDropdown(false);
    navigate('/profile');
  };

  // Group chat notifications by sender
  const groupedNotifications = useMemo(() => {
    const chatGroups = new Map();
    const result = [];

    for (const notif of notifications) {
      const isChat = notif.type === 'chat' || (notif.title && notif.title.toLowerCase().includes('new message from'));
      if (isChat) {
        const senderKey = notif.metadata?.chatUserId || notif.metadata?.senderName || notif.title;
        if (chatGroups.has(senderKey)) {
          const existing = chatGroups.get(senderKey);
          existing.count += 1;
          existing.ids.push(notif._id);
          if (!notif.isRead) existing.hasUnread = true;
        } else {
          const groupObj = {
            ...notif,
            count: 1,
            ids: [notif._id],
            hasUnread: !notif.isRead,
          };
          chatGroups.set(senderKey, groupObj);
          result.push(groupObj);
        }
      } else {
        result.push({
          ...notif,
          count: 1,
          ids: [notif._id],
          hasUnread: !notif.isRead,
        });
      }
    }

    return result;
  }, [notifications]);

  // Handle notification click - mark as read and navigate
  const handleNotificationClick = (notif) => {
    setShowNotifDropdown(false);

    // Navigate immediately based on notification link
    if (notif.link) {
      navigate(notif.link);
    }

    const token = localStorage.getItem("adminToken");
    const targetIds = notif.ids && notif.ids.length > 0 ? notif.ids : [notif._id];

    // Remove grouped IDs from list
    setNotifications((prev) => prev.filter((n) => !targetIds.includes(n._id)));

    // Mark as read in background for all grouped IDs
    targetIds.forEach((id) => {
      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notifications/mark-read/${id}`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
      }).catch((error) => {
        console.error("Error marking notification as read:", error);
      });
    });
  };

  // Handle individual notification delete
  const handleDeleteNotification = (e, notif) => {
    e.stopPropagation();
    const token = localStorage.getItem("adminToken");
    const targetIds = notif.ids && notif.ids.length > 0 ? notif.ids : [notif._id];

    setNotifications((prev) => prev.filter((n) => !targetIds.includes(n._id)));

    targetIds.forEach((id) => {
      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notifications/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).catch((error) => {
        console.error("Error deleting notification:", error);
      });
    });

    toast.success("Notification deleted");
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notifications/mark-all-read`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ forAdmin: true }),
      });

      setNotifications([]);
      setShowNotifDropdown(false);
      toast.info("All notifications marked as read");
    } catch (error) {
      console.error("Error marking all as read:", error.message);
      toast.error("Failed to mark notifications as read");
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <header className="sticky top-0 z-30 h-16 bg-gradient-to-r from-black via-dark-900 to-primary-900 text-white dark:from-dark-950 dark:via-dark-900 dark:to-dark-800 backdrop-blur-xl border-b border-white/10 dark:border-dark-700 shadow-lg transition-colors duration-300">
      <div className="flex items-center justify-between h-full px-4 md:px-6">
        {/* Left Section */}
        <div className="flex items-center gap-4">
          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2 rounded-xl text-white hover:bg-white/10 dark:text-gray-300 dark:hover:bg-dark-700 transition-all duration-200"
            onClick={() => setSidebarOpen(true)}
          >
            <FaBars className="w-5 h-5" />
          </button>
          
          <div className="hidden sm:flex items-center text-sm">
            <Breadcrumbs variant="header" />
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-2 md:gap-4 text-white">
          <ThemeCustomizer />

          {/* Full Screen Toggle */}
          <button 
            onClick={toggleFullscreen}
            className="p-2.5 rounded-xl bg-white/10 text-white dark:bg-dark-700 dark:text-gray-300 hover:bg-white/20 dark:hover:bg-dark-600 transition-all duration-300 group"
            title={isFullscreen ? "Exit Full Screen" : "Enter Full Screen"}
          >
            {isFullscreen ? <FaCompress className="w-5 h-5" /> : <FaExpand className="w-5 h-5" />}
          </button>

          {/* Notifications */}
          <div className="relative">
            <button 
              className="notification-btn relative p-2.5 rounded-xl bg-white/10 text-white dark:bg-dark-700 dark:text-gray-300 hover:bg-white/20 dark:hover:bg-dark-600 transition-all duration-200"
              onClick={() => {
                setShowProfileDropdown(false);
                setShowNotifDropdown((prev) => !prev);
              }}
            >
              <FaBell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs font-bold text-white bg-gradient-to-r from-red-500 to-rose-600 rounded-full shadow-lg animate-pulse">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Notification Dropdown */}
            {showNotifDropdown && (
              <div 
                ref={notifDropdownRef}
                className="absolute -right-14 sm:right-0 mt-2 w-[300px] sm:w-96 bg-white dark:bg-dark-800 rounded-2xl shadow-3d border border-gray-100 dark:border-dark-700 overflow-hidden animate-fade-in z-50"
              >
                {/* Header */}
                <div className="px-4 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">Notifications</h3>
                    <p className="text-xs text-white/70">{unreadCount} unread</p>
                  </div>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors"
                    >
                      <FaCheck className="w-3 h-3" />
                      Mark all read
                    </button>
                  )}
                </div>
                
                {/* Notifications List */}
                <div className="max-h-80 overflow-y-auto">
                  {groupedNotifications.length > 0 ? (
                    groupedNotifications.slice(0, 20).map((notif) => {
                      const style = getNotifStyle(notif.type);
                      const IconComp = style.icon;
                      return (
                        <div 
                          key={notif._id} 
                          className="group px-4 py-3 border-b border-gray-100 dark:border-dark-700 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors cursor-pointer flex items-start gap-3"
                          onClick={() => handleNotificationClick(notif)}
                        >
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${style.bg}`}>
                            <IconComp className={`w-4 h-4 ${style.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">{notif.title}</p>
                              {notif.count > 1 && (
                                <span className="px-1.5 py-0.5 text-[10px] font-extrabold bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-full flex-shrink-0">
                                  {notif.count > 9 ? '9+' : `${notif.count}+`}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{notif.message}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{timeAgo(notif.createdAt)}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0 mt-1">
                            {notif.hasUnread && (
                              <span className="w-2.5 h-2.5 rounded-full bg-blue-500 shrink-0" />
                            )}
                            <button
                              type="button"
                              onClick={(e) => handleDeleteNotification(e, notif)}
                              className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
                              title="Delete notification"
                            >
                              <FaTrashAlt className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="px-4 py-8 text-center">
                      <FaBell className="w-8 h-8 text-gray-300 dark:text-dark-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">No new notifications</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="p-2 border-t border-gray-100 dark:border-dark-700 bg-gray-50/50 dark:bg-dark-900/50 text-center">
                  <button
                    onClick={() => {
                      setShowNotifDropdown(false);
                      navigate('/notifications');
                    }}
                    className="w-full py-2 text-xs font-bold text-primary-600 dark:text-primary-400 hover:text-primary-700 hover:bg-white dark:hover:bg-dark-800 rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    View All Notifications <FaChevronRight className="w-2.5 h-2.5" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Admin Profile Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setShowNotifDropdown(false);
                setShowProfileDropdown((prev) => !prev);
              }}
              className="profile-menu-btn flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-all border border-white/10"
              title="Account menu"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center overflow-hidden shrink-0 text-sm font-semibold">
                {adminProfile.profileImage ? (
                  <img src={adminProfile.profileImage} alt={displayName} className="w-full h-full object-cover" />
                ) : (
                  adminInitial
                )}
              </div>
              <div className="hidden md:block text-left min-w-0 max-w-[140px]">
                <p className="text-sm font-medium truncate leading-tight">{displayName}</p>
                {profileSubtitle && (
                  <p className="text-[10px] text-white/60 truncate leading-tight">{profileSubtitle}</p>
                )}
              </div>
              <FaChevronDown className={`w-3 h-3 text-white/70 hidden sm:block transition-transform ${showProfileDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showProfileDropdown && (
              <div
                ref={profileDropdownRef}
                className="absolute right-0 mt-2 w-64 bg-white dark:bg-dark-800 rounded-2xl shadow-3d border border-gray-100 dark:border-dark-700 overflow-hidden animate-fade-in z-50"
              >
                <div className="px-4 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center overflow-hidden text-lg font-bold shrink-0">
                      {adminProfile.profileImage ? (
                        <img src={adminProfile.profileImage} alt={displayName} className="w-full h-full object-cover" />
                      ) : (
                        adminInitial
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{displayName}</p>
                      {profileSubtitle && (
                        <p className="text-xs text-white/75 truncate">{profileSubtitle}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="py-1">
                  <button
                    type="button"
                    onClick={handleProfileNavigate}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
                  >
                    <FaUser className="w-4 h-4 text-primary-500" />
                    <span>My Profile</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors border-t border-gray-100 dark:border-dark-700"
                  >
                    <FaSignOutAlt className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;